import batteryData from "./batteryData";
import invertersData from "./invertersData";
import eBikesData from "./eBikesData";
import bessData from "./bessData";

/** Default image when a product has no image or load fails. */
export const NAV_PRODUCT_PLACEHOLDER_IMAGE =
  "assets/img/product-3/product-placeholder.svg";

/**
 * Nav-specific product images (aligned with homepage product carousels).
 */
const NAV_PRODUCT_IMAGES = {
  "2.5kwh-lifepo4": "assets/img/product-3/battery-img-2.5.webp",
  "5kwh-lifepo4": "assets/img/product-3/battery-img.webp",
  "atp-3k-sp": "assets/img/product-3/inverter-3.5.webp",
  "atp-6k-sp": "assets/img/product-3/inverter-1.webp",
  "atp-10k-sp": "assets/img/product-3/inverter-2.webp",
  "atp-12k-h3p": "assets/img/product-3/inverter-2.webp",
  "atp-20k-h3p": "assets/img/product-3/inverter-20.png",
  "eco-dash": "assets/img/product-3/eco-dash.png",
  "eco-cruise": "assets/img/product-3/eco-cruise.png",
  "eco-powerbolt": "assets/img/product-3/eco-powerbolt.png",
  "atp-105kwh-bess": "assets/img/product-3/atp-105kwh-bess.png",
};

/** Display labels match existing navbar link text. */
const NAV_PRODUCT_LABELS = {
  "atp-3k-sp": "ATP 3.5kW Hybrid Inverter - SP",
  "atp-6k-sp": "ATP 6kW Hybrid Inverter - SP",
  "atp-10k-sp": "ATP 10kW Hybrid Inverter - SP",
  "atp-12k-h3p": "ATP 12kW Hybrid Inverter - TP",
  "atp-20k-h3p": "ATP 20kW Hybrid Inverter - TP",
};

function extractShortDescription(description) {
  if (typeof description === "string") {
    return description;
  }
  if (Array.isArray(description) && description.length > 0) {
    const first = description.find((item) => item.text) || description[0];
    return first && first.text ? first.text : "";
  }
  return "";
}

function mapBatteryProduct(battery) {
  return {
    id: battery.id,
    name: battery.name,
    route: `/solar-battery/${battery.id}`,
    description: extractShortDescription(battery.description),
    image: NAV_PRODUCT_IMAGES[battery.id] || NAV_PRODUCT_PLACEHOLDER_IMAGE,
  };
}

function mapInverterProduct(inverter) {
  return {
    id: inverter.id,
    name: NAV_PRODUCT_LABELS[inverter.id] || inverter.name,
    route: `/solar-inverter/${inverter.id}`,
    description: extractShortDescription(inverter.description),
    image: NAV_PRODUCT_IMAGES[inverter.id] || NAV_PRODUCT_PLACEHOLDER_IMAGE,
  };
}

function mapEBikeProduct(eBike) {
  const galleryImage =
    eBike.gallery && eBike.gallery[0] ? eBike.gallery[0].replace(/^\//, "") : null;

  return {
    id: eBike.id,
    name: eBike.name,
    route: `/e-bike-battery/${eBike.id}`,
    description: extractShortDescription(eBike.description),
    image:
      NAV_PRODUCT_IMAGES[eBike.id] ||
      (galleryImage ? galleryImage : NAV_PRODUCT_PLACEHOLDER_IMAGE),
  };
}

function mapBessProduct() {
  return {
    id: bessData.id,
    name: "ATP 105kWh BESS",
    route: `/industrial-solutions/${bessData.id}`,
    description: extractShortDescription(bessData.description),
    image: NAV_PRODUCT_IMAGES[bessData.id] || NAV_PRODUCT_PLACEHOLDER_IMAGE,
  };
}

/**
 * Product categories and items used by the navbar mega menu.
 * Single source of truth for desktop preview and mobile nested menus.
 */
export const NAV_PRODUCT_CATEGORIES = [
  {
    id: "solar-battery",
    label: "Solar Battery",
    products: batteryData.map(mapBatteryProduct),
  },
  {
    id: "solar-inverter",
    label: "Solar Inverter",
    products: invertersData.map(mapInverterProduct),
  },
  {
    id: "e-bike-battery",
    label: "E-Bike Battery",
    products: eBikesData.map(mapEBikeProduct),
  },
  {
    id: "industrial-solutions",
    label: "Industrial Solutions",
    products: [mapBessProduct()],
  },
];

/** Flat list of every navigable product (used for default preview selection). */
export const NAV_ALL_PRODUCTS = NAV_PRODUCT_CATEGORIES.reduce(
  (items, category) => items.concat(category.products),
  []
);

/**
 * Resolves a public URL for a nav product image.
 * @param {string} publicUrl - process.env.PUBLIC_URL + "/"
 * @param {string} imagePath - relative asset path
 */
export function resolveNavProductImageUrl(publicUrl, imagePath) {
  if (!imagePath) {
    return publicUrl + NAV_PRODUCT_PLACEHOLDER_IMAGE;
  }
  const normalized = imagePath.replace(/^\//, "");
  if (normalized.startsWith("http")) {
    return normalized;
  }
  return publicUrl + normalized;
}
